import { observer } from "mobx-react-lite";
import { useState, type FormEvent } from "react";

import { useStores } from "../../stores/RootStore.ts";
import { workerKey } from "../../stores/Worker.ts";
import { Button, Card, ErrorText, Input, Label, Muted, Page, TextArea, Title } from "../ui/primitives.tsx";

export const StartPage = observer(function StartPage() {
    const { connection, workers, ui } = useStores();
    const controllers = [...workers.controllers].sort();
    const [controller, setController] = useState(connection.main ?? controllers[0] ?? "");
    const [task, setTask] = useState("");
    const [image, setImage] = useState("mainion-worker");
    const [error, setError] = useState<string>();
    const [busy, setBusy] = useState(false);

    async function start(event: FormEvent) {
        event.preventDefault();
        setBusy(true);
        const { llmUrl, llmKey, llmModel } = connection.settings;

        const env = Object.fromEntries(
            Object.entries({ MAINION_LLM_URL: llmUrl, MAINION_LLM_KEY: llmKey, MAINION_LLM_MODEL: llmModel }).filter(([, value]) => value),
        );

        const reply = await connection.command({ type: "worker.start", controller, image, command: ["mainion-agent", task], env });

        setBusy(false);
        if (reply.error || !reply.worker) return setError(reply.error ?? "The controller didn't start a worker.");
        ui.show({ name: "worker", key: workerKey(controller, reply.worker) });
    }

    return (
        <Page as="form" onSubmit={start}>
            <Card>
                <Title>New task</Title>
                <Label>
                    Task
                    <TextArea
                        value={task}
                        onChange={(event) => setTask(event.target.value)}
                        placeholder="What should the minion do?"
                        required
                    />
                </Label>
                <Label>
                    Controller
                    <Input as="select" value={controller} onChange={(event) => setController(event.target.value)} required>
                        {controllers.map((name) => (
                            <option key={name} value={name}>
                                {name}
                                {name === connection.main ? " (main)" : ""}
                            </option>
                        ))}
                    </Input>
                </Label>
                <Label>
                    Image
                    <Input value={image} onChange={(event) => setImage(event.target.value)} required />
                </Label>
                {!connection.settings.llmUrl && <Muted>No LLM is set up yet. Add one in Settings.</Muted>}
                {error && <ErrorText>{error}</ErrorText>}
                <Button $primary type="submit" disabled={busy || !controller}>
                    {busy ? "Starting…" : "Start"}
                </Button>
            </Card>
        </Page>
    );
});
